import type { OnboardingDraft } from './useOnboardingDraft';
import { normalizeUrl } from './SourcesStep';

const MAX_NAME_CHARS = 60;

function truncateAtWord(text: string): string {
  if (text.length <= MAX_NAME_CHARS) return text;
  const cut = text.slice(0, MAX_NAME_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 20 ? cut.slice(0, lastSpace) : cut).trim();
}

/**
 * A project name nobody typed: the website's domain ("acme.com" → "Acme"),
 * else a top-level `# Heading` of the description, else its first sentence
 * of prose. The story templates' headings are `##`, so they are skipped on
 * purpose — "Product Overview" is not a name.
 */
export function suggestProjectName(draft: OnboardingDraft): string {
  if (draft.websiteUrl.trim()) {
    try {
      const host = new URL(normalizeUrl(draft.websiteUrl)).hostname.replace(/^www\./, '');
      const [label] = host.split('.');
      if (label) return label.charAt(0).toUpperCase() + label.slice(1);
    } catch {
      // not a URL — fall through to the description
    }
  }

  const lines = draft.description
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const h1 = lines.find((l) => /^#\s+\S/.test(l));
  if (h1) return truncateAtWord(h1.replace(/^#\s+/, '').replace(/[*_`]/g, ''));

  const prose = lines.find((l) => !/^#{1,6}\s/.test(l))?.replace(/^[>*\-\d.\s]+/, '').replace(/[*_`]/g, '') ?? '';
  const sentence = prose.split(/(?<=[.!?])\s/)[0] ?? prose;
  return truncateAtWord(sentence.replace(/[.!?]+$/, '')) || 'New idea';
}
